import moment from 'moment';
import {PeriodType} from 'app/original-models/period-type.model';
import { isEuropeanLanguageCode } from 'app/services/utils/locale-format.util';
import { formatNumber } from '@angular/common';

/**
 * A tiny LRU cache based on Map iteration order.
 * - get() promotes the key to most-recently used.
 * - set() evicts the least-recently used item when capacity is exceeded.
 */
export class SimpleLRU<K, V> {
    private map = new Map<K, V>();

    constructor(private capacity: number) {
    }

    get size(): number {
        return this.map.size;
    }

    get(key: K): V | undefined {
        if (!this.map.has(key)) return undefined;
        const value = this.map.get(key)!;
        // Promote to most recently used by reinserting
        this.map.delete(key);
        this.map.set(key, value);
        return value;
    }

    set(key: K, value: V): void {
        if (this.map.has(key)) {
            this.map.delete(key);
        }
        this.map.set(key, value);
        this.evictIfNeeded();
    }

    delete(key: K): boolean {
        return this.map.delete(key);
    }

    clear(): void {
        this.map.clear();
    }

    keys(): IterableIterator<K> {
        return this.map.keys();
    }

    private evictIfNeeded(): void {
        while (this.map.size > this.capacity) {
            // Delete least-recently used = first inserted
            const firstKey = this.map.keys().next().value as K | undefined;
            if (firstKey === undefined) return;
            this.map.delete(firstKey);
        }
    }
}

export class Utils {
    static datesDiff(date1: Date, date2: Date, periodType: any) {
        let mDate1 = moment(date1);
        let mDate2 = moment(date2);
        switch (periodType) {
            case PeriodType.Daily:
                mDate1.startOf('day');
                mDate2.startOf('day');
                return mDate1.diff(mDate2, 'days');
            case PeriodType.Monthly:
                mDate1.startOf('month');
                mDate2.startOf('month');
                return mDate1.diff(mDate2, 'months');
            case PeriodType.Weekly:
                mDate1.startOf('isoWeek');
                mDate2.startOf('isoWeek');
                //let wDate = weekDate ? weekDate.startOf('weeks') : null;
                return mDate1.diff(mDate2, 'weeks');
            case PeriodType.Quarterly:
                mDate1.startOf('quarter');
                mDate2.startOf('quarter');
                return mDate1.diff(mDate2, 'quarters');
            case PeriodType.Yearly:
            case PeriodType.PlanPeriod:
                mDate1.startOf('year');
                mDate2.startOf('year');
                return mDate1.diff(mDate2, 'years');
        }
    }

    static floatToString(number: any, locale: string, minDecimalPlaces?: number): string {
        if (number == null || number === '') {
            return number;
        }
        
        // Convert to number, handling locale-specific decimal separator
        let numValue: number;
        if (typeof number === 'string') {
            const normalizedValue = number.replace(',', '.');
            numValue = parseFloat(normalizedValue);
        } else {
            numValue = Number(number);
        }
        
        // If not a valid number, return as-is
        if (!Number.isFinite(numValue)) {
            return number;
        }
        
        // Use Angular's formatNumber with appropriate decimal places
        // If minDecimalPlaces is specified, enforce exactly that many decimal places
        // Otherwise, let Angular formatNumber use default behavior (preserves significant decimals)
        const digitsInfo = minDecimalPlaces != null && minDecimalPlaces > 0
            ? `1.${minDecimalPlaces}-${minDecimalPlaces}`
            : '1.0-10'; // Allow 0-10 decimal places, Angular will preserve significant ones
        
        return formatNumber(numValue, locale, digitsInfo);
    }

    static stringToFloat(number: any, locale: string): string {
        if (number && isEuropeanLanguageCode(locale)) {
            return number.toString().replace(',', '.');
        } else {
            return number;
        }
    }

    static dateExcelExport(): string {
        let date = new Date(),
            day = ('0' + date.getDate()).slice(-2),
            month = ('0' + (date.getMonth() + 1)).slice(-2),
            year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    static dateSelectedExcelExport(date: Date): string {
        let day = ('0' + date.getDate()).slice(-2),
            month = ('0' + (date.getMonth() + 1)).slice(-2),
            year = date.getFullYear();
        return `${day}.${month}.${year}`;
    }

    static stringToNumber(amount: any) {
        if (typeof amount === 'number') {
            return amount;
        }
        let isNegative = (/-[\d,.]+/g).test(amount);
        amount = amount.replace(/[^\d,.]/g, '').split(/[,.]/g);
        let last = amount.splice(amount.length - 1, 1);
        amount = amount.join('');
        if (amount && last && (last[0].length < 3 || last[0].length > 3) && last[0].length > 0) {
            amount += '.'
        }
        amount += last;
        amount = Number(amount);
        if (isNegative) {
            amount = amount * -1;
        }

        return amount;
    }

    static stringLocalToNumber(amount: string, languageLocale: string) {
        if (!amount) {
            return null;
        }
        if (isEuropeanLanguageCode(languageLocale)) {
            let valid = this.validateAmount(amount.toString(), /[.]/g, /[,]/g, '.', ',');
            if (!valid) {
                return NaN;
            }
            return Number(amount.toString().replace(/[.]/g, '').replace(/[,]/g, '.'));
        } else {
            let valid = this.validateAmount(amount.toString(), /[,]/g, /[.]/g, ',', '.');
            if (!valid) {
                return NaN;
            }
            return Number(amount.toString().replace(/[,]/g, ''));
        }
    }

    static validateAmount(amount: string, intSeparator: RegExp, decimalSeparator: RegExp, intChar: string, decimalChar: string) {
        let parts = amount && amount.includes(intChar) ? amount.split(intSeparator) : [amount];
        let invalid = false;
        parts.forEach((element, i) => {
            if (i > 0) {
                if (i === parts.length - 1 && element.split(decimalSeparator)) {
                    let last = element.split(decimalSeparator);
                    if (last) {
                        if (last.length > 2) {
                            invalid = true;
                            return;
                        } else if (last[0].length !== 3) {
                            invalid = true;
                            return;
                        }
                    }
                } else if (element.length !== 3) {
                    invalid = true;
                    return;
                }
            }
        });
        return !invalid;
    }

    static numberToInput(amount: number, languageLocale: string, withDecimal?: boolean, precision?: any) {
        if (!amount) {
            return 0;
        }
        let value = withDecimal
            ? amount.toLocaleString(languageLocale, precision || {minimumFractionDigits: 2, maximumFractionDigits: 2})
            : amount.toLocaleString(languageLocale);
        if (value) {
            if (isEuropeanLanguageCode(languageLocale)) {
                value = value.replace(/[.]/g, '');
            } else {
                value = value.replace(/[,]/g, '');
            }
        }
        return value;
    }

    static parseAmount(amount: string, languageLocale: string) {
        let result = this.stringLocalToNumber(amount, languageLocale);
        if (isNaN(result)) {
            //if not valid number, try parse in other locale
            result = this.stringLocalToNumber(amount, isEuropeanLanguageCode(languageLocale) ? 'en-US' : 'de-DE');
        }
        return result;
    }

    static sortPlans(plans): any[] {
        let system = [];
        let scenarios = [];
        let commited = [];
        let draft = [];
        let other = [];
        plans.forEach(plan => {
            if (plan.model.name === 'Actuals' || plan.model.name === 'Forecast' || plan.model === 'NoBase') {
                system.push(plan);
            } else if (plan.planType === 'SCENARIO') {
                scenarios.push(plan);
            } else if (plan.model.isCommitted) {
                commited.push(plan);
            } else if (plan.model.isCommitted === false) {
                draft.push(plan);
            } else {
                other.push(plan)
            }
        })
        let compare = (a, b) => {
            const titleA = a.model.name.toUpperCase();
            const titleB = b.model.name.toUpperCase();

            let comparison = 0;
            if (titleA > titleB) {
                comparison = 1;
            } else if (titleA < titleB) {
                comparison = -1;
            }
            return comparison;
        }
        return [...system, ...scenarios.sort(compare), ...commited.sort(compare), ...draft.sort(compare), ...other];
    }


    static sortByAlphabet(items: any[], ordering: 'ASC' | 'DESC' = 'ASC', sortingField?: string): string[] {
        return items.filter(i => i != null).sort(function (a, b) {
            const compare = (first: any, second: any) => {
                if (ordering === 'DESC') {
                    return second.localeCompare(first);
                } else {
                    return first.localeCompare(second);
                }
            };

            if (sortingField) {
                return compare(a[sortingField], b[sortingField]);
            } else {
                return compare(a, b);
            }
        });
    }

    static removeElementById(elements, targetId) {
        for (let i = 0; i < elements.length; i++) {
            if (elements[i].id === targetId) {
                elements.splice(i, 1);
                return elements;
            } else if (elements[i].subcategories && elements[i].subcategories.length > 0) {
                elements[i].subcategories = this.removeElementById(elements[i].subcategories, targetId);
            }
        }
        return elements;
    }

    static checkChangeAmount(item: any, type: string, locale: string) {
        let key = type.toLowerCase();
        if (item[`${type}InValid`]) {
            item[`${type}InValid`] = false;
        }
        if (item[`${key}`]) {
            let str = item[`${key}`];
            let result = Utils.stringLocalToNumber(str, locale);
            let decimalPartLength = (result.toString().split('.')[1] || '').length;
            if (isNaN(result) || decimalPartLength > 2) {
                item[`${type}InValid`] = true;
            }
            return result;
        }
    }

    static calcExpectedDate(data) {
        if (moment(data.due_date, 'YYYY-MM-DD').isBefore(moment().startOf('day'))) {
            data.expected_date = moment().startOf('day').format('YYYY-MM-DD');
        } else {
            data.expected_date = data.due_date;
        }
    }

    static getCookie(name: string): string | null {
        if (!document.cookie) {
            return null;
        }
        const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
        return match && match.length > 2 ? match[2] : null;
    }

}
